import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  Tabs,
  Tab,
  Chip,
  Stack,
  Tooltip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import type { GridColDef, GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { API_URL } from '../config';
import { apiGet } from '../utils/apiUtils';
import { useSeasonYear } from '../context/SeasonYearContext';
import { currencyFormatter } from '../utils/formatters';
import { TableSkeleton } from '../components/common/SkeletonLoaders';
import { EmptyState } from '../components/common/EmptyState';
import type {
  LostRevenueDetail,
  LostRevenueDriver,
  LostRevenueLapsedDriver,
} from '../types/lostRevenue';

const dataGridSx = {
  border: 'none',
  fontFamily: 'inherit',
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: 'transparent',
    borderBottom: '1px solid',
    borderColor: 'divider',
    minHeight: '40px !important',
    maxHeight: '40px !important',
    lineHeight: '40px !important',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontSize: '0.75rem',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: 'text.secondary',
  },
  '& .MuiDataGrid-columnSeparator': { display: 'none' },
  '& .MuiDataGrid-cell': {
    padding: '0 12px',
    fontSize: '0.875rem',
    color: 'text.primary',
    borderBottom: '1px solid',
    borderColor: 'grey.100',
  },
  '& .MuiDataGrid-row': {
    transition: 'background-color 120ms',
    '&:hover': { backgroundColor: 'action.hover' },
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: '1px solid',
    borderColor: 'divider',
    minHeight: '44px',
  },
};

const formatHometown = (hometown: string | null, state: string | null): string =>
  [hometown, state].filter(Boolean).join(', ');

const fullName = (row: { first_name: string; last_name: string }): string =>
  `${row.first_name} ${row.last_name}`;

interface GroupRow {
  key: string;
  label: string;
  non_member_count: number;
  lost_revenue: number;
}

const GroupTable: React.FC<{ title: string; rows: GroupRow[] }> = ({ title, rows }) => (
  <Box>
    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
      {title}
    </Typography>
    {rows.length === 0 ? (
      <Typography variant="body2" color="text.secondary">
        No data for this season.
      </Typography>
    ) : (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{title.replace(/^By /, '')}</TableCell>
              <TableCell align="right">Non-Members</TableCell>
              <TableCell align="right">Lost Revenue</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.key}>
                <TableCell>{row.label}</TableCell>
                <TableCell align="right">{row.non_member_count}</TableCell>
                <TableCell align="right">{currencyFormatter.format(row.lost_revenue)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )}
  </Box>
);

export const LostRevenueDetailPage: React.FC = () => {
  const { divisionId } = useParams<{ divisionId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const seasonYear = useSeasonYear();

  const yearParam = searchParams.get('year');
  const parsedYear = yearParam !== null ? Number(yearParam) : NaN;
  const year = Number.isInteger(parsedYear) && parsedYear > 0 ? parsedYear : seasonYear;

  const [data, setData] = useState<LostRevenueDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState(0);

  const fetchDetail = useCallback(async () => {
    if (!divisionId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await apiGet<LostRevenueDetail>(
        `${API_URL}/dashboard/lost-revenue/${divisionId}?year=${year}`,
      );
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load lost revenue detail.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [divisionId, year]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const driverColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Driver',
        flex: 1,
        minWidth: 180,
        valueGetter: (params: GridValueGetterParams<unknown, LostRevenueDriver>) => fullName(params.row),
      },
      { field: 'car_number', headerName: 'Car #', width: 80 },
      {
        field: 'hometown',
        headerName: 'Hometown',
        flex: 1,
        minWidth: 160,
        valueGetter: (params: GridValueGetterParams<unknown, LostRevenueDriver>) =>
          formatHometown(params.row.hometown, params.row.state),
      },
      { field: 'races_run', headerName: 'Races', width: 90, type: 'number' },
      { field: 'distinct_events', headerName: 'Events', width: 90, type: 'number' },
      { field: 'distinct_tracks', headerName: 'Tracks', width: 90, type: 'number' },
      { field: 'seasons_as_non_member', headerName: 'Seasons', width: 100, type: 'number' },
      {
        field: 'flags',
        headerName: 'Flags',
        width: 240,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<unknown, LostRevenueDriver>) => (
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, py: 0.5 }}>
            {params.row.is_repeat_offender && (
              <Chip
                label={`Repeat ${params.row.seasons_as_non_member}x`}
                size="small"
                color="warning"
                variant="outlined"
                sx={{ height: 18, fontSize: '0.65rem' }}
              />
            )}
            {params.row.also_non_member_in.map((d) => (
              <Chip
                key={d.division_id}
                label={`Also ${d.division_code}`}
                size="small"
                variant="outlined"
                sx={{ height: 18, fontSize: '0.65rem' }}
              />
            ))}
          </Stack>
        ),
      },
    ],
    [],
  );

  const lapsedColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Driver',
        flex: 1,
        minWidth: 180,
        valueGetter: (params: GridValueGetterParams<unknown, LostRevenueLapsedDriver>) => fullName(params.row),
      },
      { field: 'car_number', headerName: 'Car #', width: 80 },
      {
        field: 'hometown',
        headerName: 'Hometown',
        flex: 1,
        minWidth: 160,
        valueGetter: (params: GridValueGetterParams<unknown, LostRevenueLapsedDriver>) =>
          formatHometown(params.row.hometown, params.row.state),
      },
      { field: 'distinct_events_this_year', headerName: 'Events', width: 90, type: 'number' },
      { field: 'last_member_year', headerName: 'Last Member', width: 120, type: 'number' },
    ],
    [],
  );

  const regionRows: GroupRow[] = useMemo(
    () =>
      (data?.by_region ?? []).map((r) => ({
        key: `region-${r.region_id ?? 'unassigned'}`,
        label: r.region_name,
        non_member_count: r.non_member_count,
        lost_revenue: r.lost_revenue,
      })),
    [data],
  );

  const seriesRows: GroupRow[] = useMemo(
    () =>
      (data?.by_series ?? []).map((s) => ({
        key: `series-${s.series_id ?? 'unassigned'}`,
        label: s.series_name,
        non_member_count: s.non_member_count,
        lost_revenue: s.lost_revenue,
      })),
    [data],
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
          color="inherit"
          onClick={() => navigate('/dashboard')}
        >
          Dashboard
        </Button>
        <Typography variant="h4" component="h1">
          {data ? `${data.summary.name} Lost Revenue` : 'Lost Revenue'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {data?.year ?? year}
        </Typography>
      </Box>

      {loading ? (
        <Paper sx={{ p: 2 }}>
          <TableSkeleton rows={6} />
        </Paper>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : !data ? (
        <EmptyState type="generic" message="No lost revenue data for this division and season." />
      ) : (
        <>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Total Lost Revenue
                </Typography>
                <Typography variant="h4" color="error.main" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                  {currencyFormatter.format(data.summary.totalLostRevenue)}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2" color="text.secondary">
                  {data.summary.totalNonMembers} non-members at {currencyFormatter.format(data.summary.price)} each
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Division {data.summary.code}
                </Typography>
              </Box>
            </Box>
          </Paper>

          <Paper>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
            >
              <Tab label={`Non-Member Drivers (${data.drivers.length})`} />
              <Tab label={`By Track (${data.by_track.length})`} />
              <Tab label="By Region & Series" />
              <Tab label={`Lapsed Members (${data.lapsed_members.length})`} />
            </Tabs>

            {tab === 0 && (
              <Box sx={{ height: 600, p: 2 }}>
                <DataGrid
                  rows={data.drivers}
                  columns={driverColumns}
                  getRowId={(row) => row.driver_id}
                  initialState={{ sorting: { sortModel: [{ field: 'races_run', sort: 'desc' }] } }}
                  components={{ Toolbar: GridToolbar }}
                  componentsProps={{
                    toolbar: {
                      showQuickFilter: true,
                      quickFilterProps: { debounceMs: 300 },
                      printOptions: { disableToolbarButton: true },
                    },
                  }}
                  disableSelectionOnClick
                  density="compact"
                  sx={dataGridSx}
                />
              </Box>
            )}

            {tab === 1 && (
              <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Drivers can race multiple tracks, so per-track counts overlap and sum to more than the division total.
                  </Typography>
                  <Tooltip title="A non-member who raced at three tracks is counted at all three." arrow placement="top">
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                  </Tooltip>
                </Box>
                {data.by_track.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No data for this season.
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Track</TableCell>
                          <TableCell>Location</TableCell>
                          <TableCell align="right">Non-Members</TableCell>
                          <TableCell align="right">Lost Revenue</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.by_track.map((track) => (
                          <TableRow key={track.track_id}>
                            <TableCell>{track.track_name}</TableCell>
                            <TableCell>{formatHometown(track.track_city, track.track_state)}</TableCell>
                            <TableCell align="right">{track.non_member_count}</TableCell>
                            <TableCell align="right">{currencyFormatter.format(track.lost_revenue)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}

            {tab === 2 && (
              <Stack spacing={3} sx={{ p: 2 }}>
                <GroupTable title="By Region" rows={regionRows} />
                <GroupTable title="By Series" rows={seriesRows} />
              </Stack>
            )}

            {tab === 3 && (
              <Box sx={{ height: 600, p: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Non-members this season who held a membership last season. The strongest re-signup targets.
                </Typography>
                {data.lapsed_members.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No lapsed members for this division and season.
                  </Typography>
                ) : (
                  <DataGrid
                    rows={data.lapsed_members}
                    columns={lapsedColumns}
                    getRowId={(row) => row.driver_id}
                    initialState={{ sorting: { sortModel: [{ field: 'distinct_events_this_year', sort: 'desc' }] } }}
                    components={{ Toolbar: GridToolbar }}
                    componentsProps={{
                      toolbar: {
                        showQuickFilter: true,
                        quickFilterProps: { debounceMs: 300 },
                        printOptions: { disableToolbarButton: true },
                      },
                    }}
                    disableSelectionOnClick
                    density="compact"
                    sx={dataGridSx}
                  />
                )}
              </Box>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};
